import type { User, UserRole } from '../types';

/**
 * Source of truth for "what RBAC roles does this user hold right now".
 *
 * The `User.roles` field is a deprecated convenience copy that AuthContext
 * keeps in sync with the active org for backward compatibility. New code
 * should always read from `user.orgs.find(o => o.is_active)?.roles` so the
 * answer follows org switches deterministically.
 *
 * IMPORTANT: these helpers cover platform-level RBAC roles ONLY (admin,
 * super_admin) — used for billing/subscription/org-management UI. They do
 * NOT bypass module-licence checks. For module features (timesheet.hr,
 * spray.admin, irrigation.admin, etc.) always use `useModuleLicense`.
 */

export function getActiveOrgRoles(user: User | null | undefined): UserRole[] | undefined {
  return user?.orgs?.find(o => o.is_active)?.roles;
}

export function isOrgAdmin(user: User | null | undefined): boolean {
  const roles = getActiveOrgRoles(user);
  return !!roles?.some(r => r === 'admin' || r === 'super_admin');
}

export function isOrgSuperAdmin(user: User | null | undefined): boolean {
  const roles = getActiveOrgRoles(user);
  return !!roles?.some(r => r === 'super_admin');
}

/**
 * Factree, organisation 1: our nursery business. Its website, home garden
 * curation and customer invites are Factree's. Mirrors `FACTREE_ORG_ID` in
 * HHNodeServer `src/config/platform.js`.
 *
 * The platform organisation (billing, super admin) is FactreeTech since
 * 2026-11-04, a different organisation whose id differs between dev and
 * production, so it is read off `orgs[].is_platform`, never an id.
 */
export const FACTREE_ORG_ID = 1;

/**
 * The organisations whose customers are in the old system's book (hh.factree):
 * Factree (1) and Murray Valley Nurseries (3). Only they bring customers and
 * balances across from it. Mirrors LEGACY_BOOK_ORG_IDS in HHNodeServer.
 */
export const LEGACY_BOOK_ORG_IDS: readonly number[] = [FACTREE_ORG_ID, 3];

export function isLegacyBookOrg(user: User | null | undefined): boolean {
  return LEGACY_BOOK_ORG_IDS.includes(Number(user?.organisation_id));
}

/**
 * Platform billing access: billing_manager or super_admin held in the
 * platform organisation.
 *
 * Billing is ours alone — it is how we give modules to other organisations
 * and control what they hold — so the same role held in a customer org does
 * not count. Unlike the helpers above this is not scoped to the ACTIVE org:
 * someone holding the role in the platform org keeps the way in whichever
 * org their tab is showing. The server (requireBillingAccess) is the real
 * gate; this only decides whether to show the way in.
 */
export function hasBillingAccess(user: User | null | undefined): boolean {
  return !!user?.orgs?.some(
    o => o.is_platform === true && o.roles?.some(r => r === 'billing_manager' || r === 'super_admin'),
  );
}

/**
 * Platform staff: super_admin held in the platform organisation. They keep
 * the shared reference data every organisation reads (the shared spray
 * situations and their grouping). The server (HHNodeServer
 * `chemicalAccess.isPlatformStaff`) is the real gate; this only decides
 * whether to offer the edit.
 */
export function isPlatformStaff(user: User | null | undefined): boolean {
  return !!user?.orgs?.some(o => o.is_platform === true && o.roles?.some(r => r === 'super_admin'));
}
